import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { OfferViewModel } from '../../core/models/offer-view.model';
import { DataLayersService } from '../../core/services/data-layers.service';
import { OfferDetailViewService } from '../../core/services/offer-detail-view.service';
import { OfferService } from '../../core/services/offer.service';
import { SesionService } from '../../core/services/sesion.service';
import { OfferDetailViewOrganismComponent } from '../../shared/ui/organisms/offer-detail-view/offer-detail-view.component';

/**
 * Página que muestra app-offer-detail-view en una pestaña propia.
 * Se abre desde "Ver más detalles" de la card de oferta (ver CardComponent.openOfferDetail).
 */
@Component({
  selector: 'app-offer-detail-page',
  imports: [OfferDetailViewOrganismComponent],
  templateUrl: './offer-detail-page.component.html',
  styleUrl: './offer-detail-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OfferDetailPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly detailView = inject(OfferDetailViewService);
  private readonly offerService = inject(OfferService);
  private readonly sesion = inject(SesionService);
  private readonly dataLayer = inject(DataLayersService);

  private readonly urlLogin = environment.urlLogin;
  // Evita que un doble clic dispare validateSoftFilters() dos veces (igual que CardComponent).
  private applyInFlight = false;

  protected readonly offer = signal<OfferViewModel | null>(null);

  ngOnInit(): void {
    const params = this.route.snapshot.paramMap;
    const companyId = params.get('companyId') ?? '';
    const offerId = params.get('offerId') ?? '';

    const offer = this.detailView.read(companyId, offerId);
    if (!offer) {
      // URL abierta directamente, compartida o recargada tras vencer el traspaso: no hay datos de la oferta
      // en esta pestaña, así que se usa la landing de la oferta, que la consulta al servicio.
      this.router.navigate(['/aliado', companyId, offerId], { replaceUrl: true });
      return;
    }

    // Misma preparación que hace la landing de la oferta para que el flujo de aplicación funcione en esta pestaña.
    this.offerService.offersList = [offer];
    this.offerService.offerSelected = offer;
    this.sesion.dataOffer['companyId'] = offer.company.id;
    this.sesion.dataOffer['offerId'] = offer.offerId;
    this.sesion.dataOffer['companyName'] = offer.company.name;
    this.sesion.dataOffer['productName'] = offer.card.productName;
    this.sesion.dataOffer['landingByOffer'] = '1';

    this.offer.set(offer);
  }

  /**
   * Mismo comportamiento que CardComponent.applyOffer(): oferta gratuita redirige al aliado, usuario con sesión
   * abre la validación de filtros/confirmación y usuario sin sesión va al login.
   */
  protected apply(): void {
    const offer = this.offer();
    if (!offer) {
      return;
    }

    localStorage.setItem('companyId', offer.company.id ?? '');
    localStorage.setItem('offerId', String(offer.offerId));
    localStorage.setItem('companyName', offer.company.name);
    localStorage.setItem('productName', offer.card.productName);
    this.offerService.offerSelected = offer;

    this.dataLayer.dl_eventoGA4_content(
      'select_content',
      offer.card.productName,
      String(offer.offerId),
      offer.company.name,
      DataLayersService.getOfferIsFeatured(offer.labelType),
      DataLayersService.getOfferLabel(offer.labelType, offer.textLabel)
    );

    if (offer.free == true) {
      const url = offer.detail.urlRedirect ?? '';
      window.location.href = url.includes('https://') || url.includes('http://') ? url : 'https://' + url;
      return;
    }

    if (this.sesion.isIdsession()) {
      const name = offer.company.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const productName = offer.card.productName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      this.dataLayer.dl_eventoGA4_event_button('clic_modal_oferta', name + '-' + productName);

      if (offer.card.buttonId != undefined && !this.applyInFlight) {
        this.applyInFlight = true;
        this.offerService.validateSoftFilters();
        setTimeout(() => { this.applyInFlight = false; }, 1500);
      }
      return;
    }

    // Sin sesión: al volver del login, la landing de la oferta (/aliado/:companyId/:offerId) retoma la aplicación.
    localStorage.removeItem('isHome');
    localStorage.setItem('landingByOffer', '1');
    window.location.href = this.urlLogin;
  }

  /**
   * "Regresar": cierra esta pestaña para volver a la que lista las ofertas. Si el navegador no permite cerrarla
   *  vuelve a la página anterior o, en su defecto, al listado de ofertas.
   */
  protected close(): void {
    window.close();

    setTimeout(() => {
      if (window.closed) {
        return;
      }

      if (window.history.length > 1) {
        window.history.back();
      } else {
        this.router.navigate([this.sesion.isIdsession() ? '/ofertas' : '/categoria/creditos']);
      }
    }, 150);
  }
}
